/**
 * Utilidades para formatear y describir planes del catálogo 2026
 */

const PLAN_ORDER = ['ESPEJO', 'HORIZONTE_60', 'ORACULO_365'];

const CURRENCY_FORMATTER = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 0
});

const PLAN_FEATURES = {
  ESPEJO: [
    'Tu calendario numerológico del mes en curso',
    'Mensajes personalizados diarios en 4 áreas clave',
    'Sincronización con tu calendario (Google, Apple, Outlook, etc.)',
    'Ideal para comenzar con tu propio perfil',
    'Perfiles Adicionales (Ninja) disponibles para comprar por separado'
  ],
  HORIZONTE_60: [
    'Visibilidad de tus 60 días numerológicos siguientes',
    'Guía semanal y mensajes personalizados diarios en 4 áreas',
    'Sincronización de 60 días con tu calendario (Google, Apple, etc.)',
    'Incluye 1 Perfil Adicional (Ninja) para ver la info de otra persona',
    'Perfiles Adicionales (Ninja) disponibles para comprar por separado'
  ],
  ORACULO_365: [
    'Visión numerológica completa de tus 365 días (año completo)',
    'Mensajes personalizados diarios en 4 áreas',
    'Sincronización del año completo con tu calendario (Google, Apple, etc.)',
    'Incluye 4 Perfiles Adicionales (Ninja) para familia o clientes',
    '🎁 Sorpresas adicionales cada mes',
    'Perfiles Adicionales (Ninja) disponibles para comprar por separado'
  ]
};

export function sortPlans(plans = []) {
  return [...plans].sort((a, b) => {
    const indexA = PLAN_ORDER.indexOf(a.planCode);
    const indexB = PLAN_ORDER.indexOf(b.planCode);
    if (indexA === -1 && indexB === -1) return a.displayName.localeCompare(b.displayName);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

export function formatCurrency(value) {
  if (value === null || value === undefined) return null;
  return CURRENCY_FORMATTER.format(Number(value));
}

export function getPlanFeatures(planCode) {
  const baseFeatures = PLAN_FEATURES[planCode] || [];
  return [
    ...baseFeatures,
    'Visualización completa de tu Etapa de Vida',
    'Revisa los números de tu Pináculo'
  ];
}

export function getHorizonLabel(plan) {
  if (!plan || !plan.horizon) return '';
  if (plan.horizon.label) return plan.horizon.label;
  if (plan.horizon.mode === 'CALENDAR_MONTH') {
    return 'Mes calendario en curso';
  }
  if (plan.horizon.mode === 'ROLLING_DAYS') {
    return `${plan.horizon.value} días corridos hacia adelante`;
  }
  return '';
}

export function describePromo(promo) {
  if (!promo || !promo.isActive || !promo.price) return null;
  const end = promo.endAt ? new Date(promo.endAt) : null;
  const formattedEnd = end ? end.toLocaleDateString('es-MX') : 'fecha limitada';
  return `Precio especial ${formatCurrency(promo.price)} anual hasta ${formattedEnd}`;
}

export function getPlanDateLimits(subscription, referenceDate = new Date()) {
  if (!subscription) return null;
  const limitDays = Number.isFinite(subscription.horizon_limit_days) ? subscription.horizon_limit_days : null;
  const planType = subscription.plan_type ? String(subscription.plan_type).toUpperCase() : null;
  
  // ✅ CRÍTICO: Obtener fecha de contratación y convertir a fecha LOCAL
  // 🌎 IMPORTANTE: La BD guarda en UTC, pero debemos usar la fecha LOCAL del usuario
  const contractDate = subscription.paid_at || subscription.paidAt || subscription.created_at || subscription.createdAt;
  let startDate;
  
  if (contractDate) {
    // Crear Date desde la cadena ISO (viene en UTC desde la BD)
    const utcDate = new Date(contractDate);
    
    // Extraer fecha LOCAL (año, mes, día) ignorando la hora UTC
    // Ejemplo: 2025-11-03T03:17:12.931Z (UTC) → 2 nov 2025 21:17 (México UTC-6)
    // Usamos .getFullYear(), .getMonth(), .getDate() que devuelven valores en hora LOCAL
    const localYear = utcDate.getFullYear();
    const localMonth = utcDate.getMonth();
    const localDay = utcDate.getDate();
    
    // Crear nueva fecha con la fecha LOCAL a medianoche
    startDate = new Date(localYear, localMonth, localDay, 0, 0, 0, 0);
    
    console.log('🌎 [CONVERSIÓN UTC→LOCAL]:', {
      contractDateUTC: contractDate,
      utcDateISO: utcDate.toISOString(),
      localYear,
      localMonth: localMonth + 1, // +1 para mostrar 1-12
      localDay,
      startDateISO: startDate.toISOString().slice(0, 10),
      startDateLocal: startDate.toLocaleDateString('es-MX')
    });
  } else {
    startDate = new Date(referenceDate);
  }
  
  console.log('📅 [getPlanDateLimits] Calculando rangos:', {
    planType,
    limitDays,
    contractDate,
    startDate: startDate.toISOString().slice(0, 10),
    startDateLocal: startDate.toLocaleDateString('es-MX'),
    referenceDate: referenceDate.toISOString().slice(0, 10),
    has_paid_at: !!subscription.paid_at,
    has_paidAt: !!subscription.paidAt,
    has_created_at: !!subscription.created_at,
    has_createdAt: !!subscription.createdAt
  });
  
  // ✅ VERIFICAR PRIMERO EL PLAN TYPE (prioridad sobre limitDays)
  // Esto previene que Oráculo 365 sea tratado como Horizonte 60
  
  // ✅ ESPEJO: Mes actual completo
  if (planType === 'ESPEJO') {
    const startMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
    const endMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0);
    const result = {
      label: 'mes en curso',
      start: toStartOfDay(startMonth),
      end: toEndOfDay(endMonth)
    };
    console.log('📅 [ESPEJO] Rango calculado:', {
      start: result.start.toISOString().slice(0, 10),
      end: result.end.toISOString().slice(0, 10)
    });
    return result;
  }
  
  // ✅ ORÁCULO 365: Desde contratación + 365 días adelante
  // BONUS: Si contratan en 2025, dar todo 2026
  // IMPORTANTE: Verificar planType primero (Oráculo puede tener limitDays=365 en DB)
  if (planType === 'ORACULO_365') {
    const end = new Date(startDate);
    end.setDate(end.getDate() + 365);
    
    // 🎁 BONUS: Si contratan en 2025, extender hasta 31 dic 2026
    const contractYear = startDate.getFullYear();
    console.log('🎁 [BONUS 2026] Verificando elegibilidad:', {
      startDate: startDate.toISOString(),
      contractYear,
      isDate: startDate instanceof Date,
      shouldGetBonus: contractYear === 2025
    });
    
    if (contractYear === 2025) {
      const end2026 = new Date(2026, 11, 31, 23, 59, 59); // 31 dic 2026
      const result = {
        label: '365 días + todo 2026',
        start: toStartOfDay(startDate),
        end: toEndOfDay(end2026)
      };
      console.log('📅 [ORÁCULO 365] Rango con BONUS 2026:', {
        contractYear,
        start: result.start.toISOString().slice(0, 10),
        end: result.end.toISOString().slice(0, 10),
        totalDays: Math.floor((result.end - result.start) / (1000 * 60 * 60 * 24))
      });
      return result;
    }
    
    const result = {
      label: '365 días',
      start: toStartOfDay(startDate),
      end: toEndOfDay(end)
    };
    console.log('📅 [ORÁCULO 365] Rango normal:', {
      start: result.start.toISOString().slice(0, 10),
      end: result.end.toISOString().slice(0, 10)
    });
    return result;
  }
  
  // ✅ HORIZONTE 60: Desde contratación + N días adelante
  // IMPORTANTE: Solo si NO es Oráculo 365 (que también puede tener limitDays)
  // 🎁 BONUS 2026: Si es plan ANUAL y contratan en 2025 → Todo 2026
  if (planType === 'HORIZONTE_60' && Number.isFinite(limitDays) && limitDays > 0) {
    const end = new Date(startDate);
    end.setDate(end.getDate() + limitDays);
    
    // 🎁 BONUS: Si es plan ANUAL y contratan en 2025, dar todo 2026
    const contractYear = startDate.getFullYear();
    const billingCycle = subscription.billing_cycle || subscription.billingCycle;
    
    console.log('🎁 [BONUS 2026 - HORIZONTE] Verificando:', {
      contractYear,
      billingCycle,
      isAnnual: billingCycle === 'annual',
      shouldGetBonus: contractYear === 2025 && billingCycle === 'annual'
    });
    
    if (contractYear === 2025 && billingCycle === 'annual') {
      const end2026 = new Date(2026, 11, 31, 23, 59, 59);
      const result = {
        label: '60 días + todo 2026',
        start: toStartOfDay(startDate),
        end: toEndOfDay(end2026)
      };
      console.log('📅 [HORIZONTE 60 ANUAL] Rango con BONUS 2026:', {
        contractYear,
        start: result.start.toISOString().slice(0, 10),
        end: result.end.toISOString().slice(0, 10)
      });
      return result;
    }
    
    const label = planType === 'HORIZONTE_60' ? '60 días' : `${limitDays} días`;
    const result = {
      label,
      start: toStartOfDay(startDate),
      end: toEndOfDay(end)
    };
    console.log('📅 [HORIZONTE 60] Rango normal:', {
      limitDays,
      billingCycle,
      start: result.start.toISOString().slice(0, 10),
      end: result.end.toISOString().slice(0, 10)
    });
    return result;
  }
  
  console.log('⚠️ [getPlanDateLimits] No se pudo determinar el rango');
  return null;
}

export function getNinjaLimit(subscription) {
  if (!subscription) return 0;
  if (Number.isFinite(subscription.ninja_limit)) return subscription.ninja_limit;
  if (Number.isFinite(subscription.max_ninja_profiles)) return subscription.max_ninja_profiles;
  return 0;
}

function toStartOfDay(date) { const d = new Date(date); d.setHours(0,0,0,0); return d; }
function toEndOfDay(date) { const d = new Date(date); d.setHours(23,59,59,999); return d; }

export function formatRangeForHumans(start, end) {
  if (!start && !end) return '';
  const startStr = start ? start.toLocaleDateString('es-MX') : '';
  const endStr = end ? end.toLocaleDateString('es-MX') : '';
  if (startStr && endStr) return `${startStr} → ${endStr}`;
  return startStr || endStr;
}
